/**
 * "Keeps getting you" — the cards currently flagged as struggling.
 *
 * Generic across all four apps, unlike the einbuergerungstest-only Library.
 * Every app can flag a card, so every app gets the list.
 *
 * The rows lead with the WRONG answers, not the counts. "You missed this nine
 * times" tells you to keep grinding; "you picked `der` all three times" is a
 * misconception you can fix in one second. It is also the only way to tell a
 * systematic error from a guess — three different wrong picks is noise, the
 * same wrong pick three times is a belief.
 *
 * Cards whose misses all predate migration 0006 carry no recorded answers, so
 * those rows fall back to the counts and say so rather than showing blanks.
 */
import { ArrowLeft, TriangleAlert } from 'lucide-react';
import { Link, useParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { StrugglingCard } from '@/core/types';
import { useAppStruggling } from '@/lib/queries';
import { articleBorder, articleText, type Article } from '@/lib/gender';
import { cn } from '@/lib/utils';

export function MistakesRoute() {
  const { appId } = useParams<{ appId: string }>();
  const query = useAppStruggling(appId);
  const cards = query.data?.cards ?? [];

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border bg-card border-b">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6">
          <Button asChild variant="ghost" size="sm">
            <Link to={`/apps/${appId}`}>
              <ArrowLeft className="size-4" />
              Back
            </Link>
          </Button>
          <h1 className="font-display text-xl font-semibold">Keeps getting you</h1>
        </div>
      </header>

      <main className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-10">
        {query.isLoading ? (
          <div className="flex flex-col gap-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-20 w-full rounded-xl" />
            ))}
          </div>
        ) : query.error ? (
          <p className="text-destructive text-center text-sm">{query.error.message}</p>
        ) : cards.length === 0 ? (
          <EmptyState appId={appId!} />
        ) : (
          <>
            <p className="text-muted-foreground text-sm">
              Missed three times in a row, and not yet answered right twice since.
            </p>

            <ul className="flex flex-col gap-3">
              {cards.map((card) => (
                <MistakeCard key={card.id} card={card} />
              ))}
            </ul>

            <div className="sticky bottom-4">
              <Button asChild size="lg" className="w-full">
                <Link to={`/apps/${appId}/practice?mode=mistakes`}>
                  Practice these {cards.length}
                </Link>
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function MistakeCard({ card }: { card: StrugglingCard }) {
  const article = (card.payload?.article ?? null) as Article | null;
  const answer = (card.translations.en ?? []).join(', ');
  const total = card.counts.correct + card.counts.incorrect;

  return (
    <li
      className={cn(
        'border-border bg-card flex flex-col gap-2 rounded-xl border border-l-4 p-4',
        articleBorder(article),
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="font-display text-lg font-semibold">
          {card.external_id ? (
            <span className="text-muted-foreground font-sans text-xs font-medium tracking-wider">
              {card.external_id} ·{' '}
            </span>
          ) : null}
          {article ? <span className={articleText(article)}>{article} </span> : null}
          {card.de}
        </span>
        {answer ? <span className="text-base">{answer}</span> : null}
      </div>

      {card.wrong_answers.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {card.wrong_answers.map((w) => (
            <li
              key={w.answer}
              className="bg-destructive/10 text-destructive inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs"
            >
              <span>{w.answer}</span>
              {w.times > 1 && <span className="opacity-70">×{w.times}</span>}
            </li>
          ))}
        </ul>
      ) : (
        // Every miss on this card predates migration 0006, when only right/wrong
        // was recorded. Say so rather than implying the answers were empty.
        <p className="text-muted-foreground text-xs italic">
          Answers weren&rsquo;t recorded for these misses yet — new ones will show here.
        </p>
      )}

      <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
        <TriangleAlert className="text-warning size-3.5" />
        {card.wrong_streak} in a row
        {total > 0 && ` · ${card.counts.incorrect} of ${total} wrong all-time`}
      </p>
    </li>
  );
}

function EmptyState({ appId }: { appId: string }) {
  return (
    <div className="flex flex-col items-center gap-4 py-16 text-center">
      <p className="font-display text-xl">Nothing is getting you right now.</p>
      <p className="text-muted-foreground max-w-sm text-sm">
        Cards land here after three misses in a row, and leave once you answer them right twice.
      </p>
      <Button asChild variant="outline">
        <Link to={`/apps/${appId}`}>Back to the app</Link>
      </Button>
    </div>
  );
}
